import { useRef, useEffect } from "react";

import { DealLaScSection } from "@/types/dealLaSc.types";
import { TableCreatorShellList } from "@FillInTableContainer/shared/types/tableCreator.types";
import IconXMark from "@images/fillInTable/tableCreator/icon-x-mark-white.svg";

import DealLaScSingleTableShell from "./components/DealLaScSingleTableShell/DealLaScSingleTableShell";
import * as S from "./DealLaScSingleGroupShell.style";
import { useHandleDealLaScSingleTableCreatorGroupShell } from "./hooks/useHandleDealLaScSingleTableCreatorGroupShell";

interface Props {
  groupTableKey: DealLaScSection;
  groupTableIndex: number;
  groupShellKey: string | null;
  shellList: TableCreatorShellList;
  tableKey: string | null;
  tableIndex: number;
  groupShellIndex: number;
  customTableDeleteEvent?: () => void;
}

const DealLaScSingleGroupShell = ({
  groupShellIndex,
  groupTableIndex,
  groupTableKey,
  groupShellKey,
  shellList,
  tableIndex,
  tableKey,
  customTableDeleteEvent,
}: Props) => {
  const selectButtonRef = useRef<HTMLDivElement>(null);
  const deleteButtonRef = useRef<HTMLButtonElement>(null);
  const positionPropsInfo = {
    groupTableKey,
    groupTableIndex,
    groupShellKey,
    groupShellIndex,
    tableKey,
    tableIndex,
  };

  const {
    focusStatus,
    isFocused,
    isClearGroupShell,
    isDeleteGroupShell,
    isAlertGroupShell,
    isEnableEdit,
    handleDefaultFocus,
    handleClickToDeleteGroupShell,
    handleFocusGroupShell,
  } = useHandleDealLaScSingleTableCreatorGroupShell({ positionPropsInfo });

  useEffect(() => {
    const handleCheckClickOutsideShell = (e: MouseEvent) => {
      if (isFocused) {
        const target = e.target as HTMLElement;
        const clickSelectButton = selectButtonRef.current?.contains(target);
        const clickDeleteButton = deleteButtonRef.current?.contains(target);
        if (clickSelectButton) return;
        if (clickDeleteButton) return;

        handleDefaultFocus();
      }
    };

    window.addEventListener("click", handleCheckClickOutsideShell);

    return () =>
      window.removeEventListener("click", handleCheckClickOutsideShell);
  }, [handleDefaultFocus, isClearGroupShell, isFocused, isDeleteGroupShell]);

  return (
    <S.GroupShellOutsideWrapper>
      {(isClearGroupShell || isDeleteGroupShell) && isEnableEdit && (
        <S.FixedDeleteButtonWrapper>
          <S.DeleteTableButtonWrapper>
            <S.DeleteTableButton
              ref={deleteButtonRef}
              onClick={() => {
                handleClickToDeleteGroupShell();
                if (focusStatus.type === "delete" && customTableDeleteEvent) {
                  customTableDeleteEvent();
                }
              }}
            >
              <img src={IconXMark} alt="button for clear Row" />
              {focusStatus.type === "clear"
                ? `Clear`
                : focusStatus.type === "delete"
                ? "Delete"
                : null}
            </S.DeleteTableButton>
          </S.DeleteTableButtonWrapper>
        </S.FixedDeleteButtonWrapper>
      )}

      <S.GroupShellWrapper
        isFocused={isFocused}
        isClearGroupShell={isClearGroupShell}
        isAlertGroupShell={isAlertGroupShell}
      >
        <S.SelectWrapper>
          <S.GroupShellSelectButtonDividerWrapper
            ref={selectButtonRef}
            onClick={handleFocusGroupShell}
            isPossibleSelected
          >
            <S.GroupShellSelectButtonDivider className="select-button divider" />
            <S.GroupShellSelectButton className="select-button button">
              SELECT
            </S.GroupShellSelectButton>
          </S.GroupShellSelectButtonDividerWrapper>
          <S.GroupShell>
            {shellList.map(({ shellKey }, shellListIndex) => (
              <DealLaScSingleTableShell
                key={`shellList-${String(shellListIndex)}`}
                groupTableKey={groupTableKey}
                groupTableIndex={groupTableIndex}
                groupShellKey={groupShellKey}
                groupShellIndex={groupShellIndex}
                shellKey={shellKey}
                shellIndex={0}
                tableKey={tableKey}
                tableIndex={tableIndex}
                isFocusedGroupShell={isFocused}
                isClearGroupShell={isClearGroupShell}
                isAlertGroupShell={isAlertGroupShell}
              />
            ))}
          </S.GroupShell>
        </S.SelectWrapper>
      </S.GroupShellWrapper>
    </S.GroupShellOutsideWrapper>
  );
};

export default DealLaScSingleGroupShell;
